import type { SeatmapData, SeatPlacementConfig, SeatPlacementResult } from '@/types';

function findDistance(x1: number, y1: number, x2: number, y2: number): number {
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
}

export function checkForHoles(
    taken: Record<number, 1>,
    ours: Record<number, 1>,
    seatmapData: SeatmapData,
    config: SeatPlacementConfig,
): SeatPlacementResult {
    const maxDistance = config.maxDistance ?? 25;
    const forceSeatSpace = config.forceSeatSpace ?? false;
    const forceEdgeSeatSelection = config.forceEdgeSeatSelection ?? false;
    const forceEdgeSeatSelectionDirection = config.forceEdgeSeatSelectionDirection ?? 0;
    const forceEdgeSeatSelectionSpace = forceSeatSpace ? 1 : 0;
    const checkHolesOverride = config.checkHolesOverride;

    const hasOurs = Object.keys(ours).length > 0;

    const earlyOk: SeatPlacementResult = {
        placement_ok: true,
        no_good_placement: false,
        right_problem: false,
        left_problem: false,
        leftmost_seat: -1,
        leftmost_seat_ours: -1,
        rightmost_seat: -1,
        rightmost_seat_ours: -1,
        seenfirst: hasOurs,
        seats_not_together: false,
    };

    if (forceSeatSpace) {
        // Fall through to full check — forceSeatSpace overrides early-exit logic
    } else if (checkHolesOverride == null) {
        if (seatmapData.render.checkHoles == '0') {
            return earlyOk;
        }
    } else if (!checkHolesOverride) {
        return earlyOk;
    }

    // Build groups — exact port of old JS check_for_holes grouping block
    type SubgroupData = {
        seats: number[];
        taken: Record<number, 1>;
        ours: Record<number, 1>;
        seat_x: number[];
        seat_y: number[];
    };
    const groups: Record<string, Record<string, SubgroupData>> = {};

    for (let i = 0; i < seatmapData.seats.length; i++) {
        const seat = seatmapData.seats[i];

        if (!groups[seat.group]) groups[seat.group] = {};

        const sgfields: string[] = [];
        for (let j = 0; j < seatmapData.fields.length; j++) {
            if (seatmapData.fields[j].distanceMultiplier > 1) {
                sgfields.push(seat.row ?? '');
            }
        }
        const subgroup = sgfields.join(',');

        if (!groups[seat.group][subgroup]) {
            groups[seat.group][subgroup] = { seats: [], taken: {}, ours: {}, seat_x: [], seat_y: [] };
        }

        groups[seat.group][subgroup].seats.push(i);
        groups[seat.group][subgroup].seat_x.push(seat.x);
        groups[seat.group][subgroup].seat_y.push(seat.y);

        if (taken[i]) groups[seat.group][subgroup].taken[i] = 1;
        if (ours[i])  groups[seat.group][subgroup].ours[i]  = 1;
    }

    const okCheck = {
        ok: true,
        failstate: undefined as string | undefined,
        space_required: undefined as number | undefined,
        mustSelectFromRowError: false as boolean,
    };

    // Auto-correction fields — captured from the first subgroup where seenfirst becomes true
    let resultCaptured = false;
    let resultSeenfirst = false;
    let resultLeft = 0;
    let resultRight = 0;
    let resultLeftmostSeat = -1;
    let resultLeftmostSeatOurs = -1;
    let resultRightmostSeat = -1;
    let resultRightmostSeatOurs = -1;

    for (const group in groups) {
      for (const subgroup in groups[group]) {
        const { seats, taken: sgTaken, ours: sgOurs, seat_x: seatsX, seat_y: seatsY } = groups[group][subgroup];

        let count = 0;
        let totalCount = 0;
        let ourCount = 0;
        let left = 0;
        let right = 0;
        let leftOfSelection = 0;
        let rightOfSelection = 0;
        let seenfirst = false;
        let counterTaken = 0;
        let counterOurs = 0;
        let lastTaken = false;
        let lastOurs = false;
        let failStateLeft = false;
        let failStateRight = false;
        let rightOfSelectionCountStart = false;
        let rightOfSelectionCountStop = false;
        let maxDistanceRight = 0;
        let maxDistanceLeft = 0;
        let maxDistanceFurtherRight = 0;
        let maxDistanceFurtherLeft = 0;
        let maxDistanceMostRight = 0;
        let maxDistanceMostLeft = 0;

        // Auto-correction trackers (local indices within this subgroup's seats array)
        let leftmostSeatLocalIdx = -1;
        let rightmostSeatLocalIdx = -1;
        let firstRightFreeLocalIdx = -1;

        for (let i = 0; i < seats.length; i++) {
            const seatIndex = seats[i];

            if (sgTaken[seatIndex] == undefined) {
            // Free seat (not taken by anyone else)
            ++count;
            ++totalCount;

            if (sgOurs[seatIndex] == undefined) {
                // Free, not ours
                if (!seenfirst) {
                    ++left;
                    ++leftOfSelection;
                }
                ++right;
                if (rightOfSelectionCountStart && !rightOfSelectionCountStop) {
                    ++rightOfSelection;
                    if (firstRightFreeLocalIdx == -1) firstRightFreeLocalIdx = i;
                }
                lastOurs = false;
            } else {
                // One of our seats
                if (!seenfirst) {
                    // Measure distances to the left of our first seat
                    if (seatsX[i - 1] != undefined) {
                        maxDistanceLeft = findDistance(seatsX[i - 1], seatsY[i - 1], seatsX[i], seatsY[i]);
                        if (seatsX[i - 2] != undefined) {
                            maxDistanceFurtherLeft = findDistance(seatsX[i - 2], seatsY[i - 2], seatsX[i - 1], seatsY[i - 1]);
                            if (seatsX[i - 3] != undefined) {
                                maxDistanceMostLeft = findDistance(seatsX[i - 3], seatsY[i - 3], seatsX[i - 2], seatsY[i - 2]);
                            }
                        }
                    }
                } else {
                    // Measure distances to the right of our current seat (overwrites each pass, leaving the last ours seat's right distances)
                    if (seatsX[i + 1] != undefined) {
                        maxDistanceRight = findDistance(seatsX[i], seatsY[i], seatsX[i + 1], seatsY[i + 1]);
                        if (seatsX[i + 2] != undefined) {
                            maxDistanceFurtherRight = findDistance(seatsX[i + 1], seatsY[i + 1], seatsX[i + 2], seatsY[i + 2]);
                            if (seatsX[i + 3] != undefined) {
                                maxDistanceMostRight = findDistance(seatsX[i + 2], seatsY[i + 2], seatsX[i + 3], seatsY[i + 3]);
                            }
                        }
                    }
                }

                // Check empty-space violations on the left side when transitioning into our block
                if (!lastOurs) {
                    if (counterTaken == 1 && (!forceSeatSpace || maxDistanceLeft > maxDistance || maxDistanceFurtherLeft > maxDistance)) {
                        failStateLeft = true;
                        okCheck.failstate = '1';
                    }
                    if (counterTaken == 0 && counterOurs != 0 && forceSeatSpace && maxDistanceLeft <= maxDistance && maxDistanceFurtherLeft <= maxDistance && maxDistanceMostLeft <= maxDistance) {
                        failStateLeft = true;
                        okCheck.failstate = '2a';
                    }
                    if (forceEdgeSeatSelection && !forceEdgeSeatSelectionDirection && counterTaken != forceEdgeSeatSelectionSpace && counterOurs != 0 && counterTaken != counterOurs) {
                        failStateLeft = true;
                        okCheck.failstate = '2d';
                        okCheck.space_required = forceEdgeSeatSelectionSpace;
                        okCheck.mustSelectFromRowError = true;
                    }
                    if (forceEdgeSeatSelection && forceEdgeSeatSelectionDirection && counterTaken != forceEdgeSeatSelectionSpace && counterOurs != 0 && counterTaken != counterOurs) {
                        failStateLeft = true;
                        okCheck.failstate = '2e';
                        okCheck.space_required = forceEdgeSeatSelectionSpace;
                        okCheck.mustSelectFromRowError = true;
                    }
                }

                seenfirst = true;
                right = 0;
                rightOfSelection = 0;
                rightOfSelectionCountStart = true;
                firstRightFreeLocalIdx = -1;

                if (leftmostSeatLocalIdx == -1) leftmostSeatLocalIdx = i;
                rightmostSeatLocalIdx = i;

                ++ourCount;
                lastOurs = true;
            }

            lastTaken = false;
        } else {
            // Taken seat
            if (count == 1 && !seenfirst) {
                count = 0;
                left = 0;
            }
            if (!seenfirst) {
                leftOfSelection = 0;
            }

            // Check empty-space violations on the right side when a taken seat follows our block
            if (!lastTaken) {
                if (counterOurs == 1 && seenfirst && (!forceSeatSpace || maxDistanceRight > maxDistance || maxDistanceFurtherRight > maxDistance)) {
                    failStateRight = true;
                    okCheck.failstate = '3';
                }
                if (counterOurs == 0 && seenfirst && forceSeatSpace && maxDistanceRight <= maxDistance && maxDistanceFurtherRight <= maxDistance && maxDistanceMostRight <= maxDistance) {
                    failStateRight = true;
                    okCheck.failstate = '4a';
                }
                if (forceEdgeSeatSelection && !forceEdgeSeatSelectionDirection && counterOurs != forceEdgeSeatSelectionSpace && seenfirst) {
                    failStateRight = true;
                    okCheck.failstate = '4d';
                    okCheck.space_required = forceEdgeSeatSelectionSpace;
                    okCheck.mustSelectFromRowError = true;
                }
                if (forceEdgeSeatSelection && forceEdgeSeatSelectionDirection && counterOurs != forceEdgeSeatSelectionSpace && seenfirst) {
                    failStateRight = true;
                    okCheck.failstate = '4e';
                    okCheck.space_required = forceEdgeSeatSelectionSpace;
                    okCheck.mustSelectFromRowError = true;
                }
            }

            lastTaken = true;
            lastOurs = false;
            if (rightOfSelectionCountStart) {
                rightOfSelectionCountStop = true;
            }
        }

        // Update run counters
        if (lastTaken) {
            counterTaken = 0;
        } else {
            ++counterTaken;
        }
        if (lastOurs) {
            counterOurs = 0;
        } else {
            ++counterOurs;
        }
    }

        // Post-loop: discard single trailing empty seat right of a taken boundary
        if (counterTaken == 1 && right == 1) {
            --count;
            right = 0;
        }

        if (failStateLeft && !(totalCount < 5 && left == 1)) {
            okCheck.ok = false;
        }
        if (failStateRight) {
            okCheck.ok = false;
        }

        if (ourCount > 0) {
            if (count - left - right - ourCount != 0) {
                okCheck.ok = false;
                okCheck.failstate = '5';
            }

            console.log('Group:', group, 'Subgroup:', subgroup, 'Left:', left, 'Right:', right, 'OurCount:', ourCount, 'TotalCount:', totalCount);

            if (totalCount > 4) {
                if (!forceSeatSpace) {
                    console.log('forceSeatSpace is false, applying standard hole checks');
                    console.log('Left:', left, 'Right:', right, 'OurCount:', ourCount, 'TotalCount:', totalCount);
                    if ((left == 1 && right > 0) || (right == 1 && left > 0)) {
                        okCheck.ok = false;
                        okCheck.failstate = '6';
                        console.log('Failing for reason 6: single empty seat between two blocks of free seats');
                        console.log('Left:', left, 'Right:', right, 'OurCount:', ourCount, 'TotalCount:', totalCount);
                    }
                } else {
                    // forceSeatSpace path — check for legal "between two reservations" before failing
                    if (
                        ((left == 1 || maxDistanceLeft > maxDistance) && maxDistanceFurtherRight <= maxDistance && rightOfSelection == 1 && counterTaken == 0) ||
                        (leftOfSelection == 1 && rightOfSelection == 1 &&
                            maxDistanceFurtherLeft <= maxDistance && maxDistanceFurtherRight <= maxDistance &&
                            maxDistanceFurtherLeft != 0 && maxDistanceFurtherRight != 0 &&
                            maxDistanceMostLeft != 0 && maxDistanceMostRight != 0)
                    ) {
                        // Selection is sandwiched between two existing reservations — this is fine
                    } else if (
                        (left == 1 && maxDistanceLeft <= maxDistance && (ourCount + counterOurs) == (seats.length - 1)) ||
                        (leftOfSelection == 2 && maxDistanceMostLeft <= maxDistance && (ourCount + counterOurs) != (seats.length - 2)) ||
                        (rightOfSelection == 1 && left > 0 && counterOurs == 1) ||
                        (rightOfSelection == 2 && leftOfSelection == 0) ||
                        ((rightOfSelection == 1 || (rightOfSelection == 2 && (maxDistanceMostRight > 0 || leftOfSelection == 2))) &&
                            (leftOfSelection == 1 || (leftOfSelection == 2 && (maxDistanceMostLeft > 0 || rightOfSelection == 2)))) ||
                        (maxDistanceFurtherRight > maxDistance && rightOfSelection == 1)
                    ) {
                        okCheck.ok = false;
                        okCheck.failstate = '7';
                    }
                }
                console.log('ForceEdgeSeatSelection:', forceEdgeSeatSelection, 'Direction:', forceEdgeSeatSelectionDirection, 'Space:', forceEdgeSeatSelectionSpace);

                if (forceEdgeSeatSelection && !forceEdgeSeatSelectionDirection && left != 0 && (left + right + ourCount) == seats.length) {
                    okCheck.ok = false;
                    okCheck.failstate = '9';
                    okCheck.space_required = forceEdgeSeatSelectionSpace;
                    okCheck.mustSelectFromRowError = true;
                }
                if (forceEdgeSeatSelection && forceEdgeSeatSelectionDirection && counterOurs != 0 && left != forceEdgeSeatSelectionSpace && right != forceEdgeSeatSelectionSpace) {
                    okCheck.ok = false;
                    okCheck.failstate = '10';
                    okCheck.space_required = forceEdgeSeatSelectionSpace;
                    okCheck.mustSelectFromRowError = true;
                }
            }
        }

        // Capture auto-correction info from the first subgroup that contains our seats
        if (seenfirst && !resultCaptured) {
            resultCaptured = true;
            resultSeenfirst = true;
            resultLeft = left;
            resultRight = right;

            resultLeftmostSeatOurs = leftmostSeatLocalIdx >= 0 ? seats[leftmostSeatLocalIdx] : -1;
            resultRightmostSeatOurs = rightmostSeatLocalIdx >= 0 ? seats[rightmostSeatLocalIdx] : -1;

            if (leftmostSeatLocalIdx > 0) {
                const leftIndex = leftmostSeatLocalIdx - 1;
                if (sgTaken[seats[leftIndex]] == undefined) resultLeftmostSeat = seats[leftIndex];
            }

            if (firstRightFreeLocalIdx >= 0) {
                resultRightmostSeat = seats[firstRightFreeLocalIdx];
            } else if (rightmostSeatLocalIdx >= 0 && rightmostSeatLocalIdx + 1 < seats.length) {
                const rightIndex = rightmostSeatLocalIdx + 1;
                if (sgTaken[seats[rightIndex]] == undefined) resultRightmostSeat = seats[rightIndex];
            }
        }
      }
    }
    console.log('okCheck:', okCheck);

    return {
        placement_ok: okCheck.ok,
        no_good_placement: !okCheck.ok && okCheck.failstate != '6',
        right_problem: resultRight == 1 && resultLeft > 0,
        left_problem: resultLeft == 1 && resultRight > 0,
        leftmost_seat: resultLeftmostSeat,
        leftmost_seat_ours: resultLeftmostSeatOurs,
        rightmost_seat: resultRightmostSeat,
        rightmost_seat_ours: resultRightmostSeatOurs,
        seenfirst: resultSeenfirst,
        seats_not_together: okCheck.failstate == '5',
        spaceRequired: okCheck.space_required,
        mustSelectFromRowError: okCheck.mustSelectFromRowError,
    };
}
